import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import CloseIcon from '@mui/icons-material/Close';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useGuideBooking } from '@/features/guide/hooks/useGuideBookings';
import { guideBookingsApi, guideBookingsKeys } from '@/features/guide/api/guide-bookings.api';
import { guideFinanceKeys } from '@/features/guide-finance/api/guide-finance.api';
import { formatGuidePrice } from '@/features/guide/utils/pricing';
import { PRICING_TYPE_LABELS } from '@/features/booking/utils/bookingPricing';
import { formatStatusLabel } from '@/shared/utils/statusBadge';
import { Button } from '@/shared/components/ui';
import { ContactLockedNotice } from '@/features/booking/components/ContactLockedNotice';
import { BookingStatus } from '@/types';
import { formatBookingWhen } from '@/shared/utils/bookingWhen';
import { TripExtensionPanel } from '@/features/trip-extension';

interface BookingDetailDrawerProps {
  bookingId: string | null;
  onClose: () => void;
  readOnly?: boolean;
}

export function BookingDetailDrawer({ bookingId, onClose, readOnly = false }: BookingDetailDrawerProps) {
  const queryClient = useQueryClient();
  const { data: booking, isLoading, isError } = useGuideBooking(bookingId);

  const completeBooking = useMutation({
    mutationFn: () => guideBookingsApi.completeBooking(bookingId!),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: guideBookingsKeys.all });
      void queryClient.invalidateQueries({ queryKey: guideFinanceKeys.all });
      onClose();
    },
  });

  return (
    <Drawer
      anchor="right"
      open={Boolean(bookingId)}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 420 } } }}
    >
      <Box sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" fontWeight={800}>
            Booking details
          </Typography>
          <IconButton onClick={onClose} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Box>

        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {isError && <Alert severity="error">Failed to load booking.</Alert>}

        {booking && (
          <>
            <Chip label={formatStatusLabel(booking.status)} size="small" sx={{ mb: 2 }} />

            <Typography variant="body2" color="text.secondary">
              Reference
            </Typography>
            <Typography fontWeight={700} sx={{ mb: 2 }}>
              #{booking.id.slice(-8).toUpperCase()}
            </Typography>

            <Typography>
              <strong>When:</strong> {formatBookingWhen(booking)}
            </Typography>
            <Typography>
              <strong>Destination:</strong> {booking.destinationLabel ?? booking.destinationName}
            </Typography>
            <Typography>
              <strong>Package:</strong> {PRICING_TYPE_LABELS[booking.pricingType]}
            </Typography>
            <Typography>
              <strong>Travelers:</strong> {booking.travelerCount}
            </Typography>
            <Typography sx={{ mt: 1 }}>
              <strong>Amount:</strong> {formatGuidePrice(booking.grossAmount, booking.currency)}
            </Typography>

            {booking.notes && (
              <Typography sx={{ mt: 1 }}>
                <strong>Notes:</strong> {booking.notes}
              </Typography>
            )}

            {booking.completedAt && (
              <Typography sx={{ mt: 1 }}>
                <strong>Completed:</strong> {new Date(booking.completedAt).toLocaleString('en-IN')}
              </Typography>
            )}

            {booking.cancelledAt && (
              <Typography sx={{ mt: 1 }}>
                <strong>Cancelled:</strong> {new Date(booking.cancelledAt).toLocaleString('en-IN')}
              </Typography>
            )}

            {booking.cancelReason && (
              <Typography sx={{ mt: 0.5 }}>
                <strong>Cancel reason:</strong> {booking.cancelReason}
              </Typography>
            )}

            {booking.disputeReason && (
              <Typography sx={{ mt: 0.5 }}>
                <strong>Dispute:</strong> {booking.disputeReason}
              </Typography>
            )}

            <Divider sx={{ my: 2 }} />

            {booking.status === BookingStatus.UPCOMING && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Upcoming trip on {formatBookingWhen(booking)}
              </Alert>
            )}

            {booking.status === BookingStatus.WAITING_FOR_TRAVELER_CONFIRMATION && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Waiting for traveler confirmation
              </Alert>
            )}

            {booking.status === BookingStatus.COMPLETED && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Trip completed
              </Alert>
            )}

            {booking.contactAccess?.state === 'locked' && (
              <Box sx={{ mb: 2 }}>
                {booking.travelerName && (
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Traveler: {booking.travelerName}
                  </Typography>
                )}
                <ContactLockedNotice audience="guide" unlocksAt={booking.contactAccess.unlocksAt} />
              </Box>
            )}

            {booking.contactAccess?.state === 'unlocked' && booking.travelerContact && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  Traveler contact
                </Typography>
                <Typography variant="body2">Name: {booking.travelerContact.name}</Typography>
                <Typography variant="body2">Phone: {booking.travelerContact.phone}</Typography>
              </Box>
            )}

            {booking.status === BookingStatus.PAYMENT_PENDING && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Payment is still pending. Traveler contact unlocks shortly before the trip once payment is confirmed.
              </Alert>
            )}

            <Divider sx={{ my: 2 }} />
            <TripExtensionPanel booking={booking} viewer="guide" readOnly={readOnly} />

            {booking.status === BookingStatus.IN_PROGRESS && !readOnly && (
              <Button
                intent="primary-dark"
                fullWidth
                sx={{ mt: 2 }}
                disabled={completeBooking.isPending}
                onClick={() => completeBooking.mutate()}
              >
                Complete booking
              </Button>
            )}
          </>
        )}
      </Box>
    </Drawer>
  );
}
